import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { postEnquiry } from "@/lib/site";
import { Reveal, SectionTag } from "@/components/Reveal";

const TOPICS = ["Kronegekko", "Spørgsmål om produkt", "Specialbygget terrarium", "Andet"];

export default function Enquiry() {
  const [form, setForm] = useState({ name: "", email: "", topic: TOPICS[0], message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setForm((f) => ({
        ...f,
        topic: e.detail.topic === "Kronegekko" ? TOPICS[0] : e.detail.topic === "Specialbygget terrarium" ? TOPICS[2] : f.topic,
        message: `Hej! Jeg er interesseret i ${e.detail.item}. `,
      }));
    };
    window.addEventListener("enquiry-topic", handler);
    return () => window.removeEventListener("enquiry-topic", handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Udfyld venligst navn, e-mail og besked.");
      return;
    }
    setSending(true);
    try {
      await postEnquiry(form);
      toast.success("Forespørgsel sendt — vi svarer som regel inden for en dag.");
      setForm({ name: "", email: "", topic: TOPICS[0], message: "" });
    } catch {
      toast.error("Noget gik galt. Prøv igen.");
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-cream placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-forest";

  return (
    <section
      id="enquiry"
      data-testid="enquiry-section"
      className="border-t border-white/10 bg-moss px-6 py-24 lg:px-16 lg:py-36"
    >
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
        <Reveal>
          <SectionTag index="05" label="Kontakt" />
          <h2 className="mt-6 font-serif text-4xl tracking-tight text-cream sm:text-5xl">
            Tal med <span className="italic text-pale">opdrætterne</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-sage">
            Spørgsmål om en morf, et produkt — eller et terrarium, du
            drømmer om? Skriv til os. Vi svarer personligt, som regel inden for en dag.
          </p>
          <div className="mt-10 space-y-2 font-mono text-xs uppercase tracking-[0.2em] text-sage">
            <p>GEKKOFUEL · Danmark</p>
            <p>Webshop · gekkofuel.dk</p>
            <p>Frederik &amp; Sesilie</p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            data-testid="enquiry-form"
            onSubmit={submit}
            className="space-y-5 border border-white/10 bg-surface p-8"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <input
                data-testid="enquiry-name-input"
                value={form.name}
                onChange={set("name")}
                placeholder="Dit navn"
                className={field}
              />
              <input
                data-testid="enquiry-email-input"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="Din e-mail"
                className={field}
              />
            </div>
            <select
              data-testid="enquiry-topic-select"
              value={form.topic}
              onChange={set("topic")}
              className={`${field} appearance-none`}
            >
              {TOPICS.map((t) => (
                <option key={t} value={t} className="bg-ink">
                  {t}
                </option>
              ))}
            </select>
            <textarea
              data-testid="enquiry-message-input"
              value={form.message}
              onChange={set("message")}
              placeholder="Fortæl os, hvad du leder efter…"
              rows={5}
              className={`${field} resize-none`}
            />
            <button
              data-testid="enquiry-submit-button"
              type="submit"
              disabled={sending}
              className="group flex w-full items-center justify-center gap-3 rounded-full bg-forest py-4 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-pale hover:text-ink disabled:opacity-60"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Send forespørgsel
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
